import React, { ChangeEvent } from 'react'
import { Slider as MuiSlider, Typography, Box, TextField } from '@mui/material'
import theme from 'lib/theme'
import { useTranslation } from 'react-i18next'

type SliderProps = {
  label: string
  ariaLabel: string
  maxValue: number
  minValue: number
  name: string
  value: number
  step?: number
  setFieldValue: (field: string, value: number | number[]) => void
}

export const Slider = ({
  label,
  ariaLabel,
  maxValue,
  minValue,
  name,
  value,
  setFieldValue,
  step,
}: SliderProps) => {
  const { t } = useTranslation()
  const handleSliderChange = (event: Event, newValue: number | number[]) => {
    setFieldValue(name, newValue)
  }

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFieldValue(
      name,
      event.target.value === '' ? 0 : Number(event.target.value)
    )
  }

  const handleBlur = () => {
    if (value < minValue) {
      setFieldValue(name, minValue)
    } else if (value > maxValue) {
      setFieldValue(name, maxValue)
    }
  }

  return (
    <Box mt={1}>
      <Typography variant='body2' sx={{ color: theme.palette.text.secondary }}>
        {label}
      </Typography>
      <Box display='flex'>
        <MuiSlider
          max={maxValue}
          min={minValue}
          onChange={handleSliderChange}
          value={typeof value === 'number' ? value : 1}
          aria-labelledby={ariaLabel}
          step={step || 1}
          sx={{
            '.MuiSlider-rail': {
              color: theme.palette.action.hover,
              opacity: 1,
            },
          }}
        />
        <Box
          display='flex'
          alignItems='center'
          width='48px'
          p={1}
          ml={2}
          sx={{ backgroundColor: theme.palette.action.hover }}>
          <TextField
            name={label}
            id={label}
            aria-label={ariaLabel + t('inputField')}
            variant='standard'
            value={value}
            onChange={handleInputChange}
            onBlur={handleBlur}
            InputProps={{ disableUnderline: true, sx: { fontWeight: 'bold' } }}
            // eslint-disable-next-line react/jsx-no-duplicate-props
            inputProps={{
              style: { textAlign: 'center' },
              'data-testid': 'Slider input',
            }}
          />
        </Box>
      </Box>
    </Box>
  )
}
